import React from 'react';
import { safeParseDate } from '../../utils/dateUtils';
import GINumericQuestion from './GINumericQuestion';
import GILongTextQuestion from './GILongTextQuestion';
import GIChoiceQuestion from './GIChoiceQuestion';
import GIDistributionQuestion from './GIDistributionQuestion';
import GIQuestionHeader from './GIQuestionHeader';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';

const GIFormResponses = ({
  formData,
  filteredFormData,
  selectedCampaign,
  fetchWithAuth,
  currentUser
}) => {
  const [filterByType, setFilterByType] = React.useState(null);
  const [collapsedMap, setCollapsedMap] = React.useState({});

  const selectStyles = useSelectStyles();

  // Fonction pour déterminer le type de question
  const getQuestionType = (type) => {
    switch (type) {
      case 1: return "Choix unique";
      case 2: return "Numérique";
      case 3: return "Texte court";
      case 4: return "Distribution";
      case 6: return "Texte long";
      case 7: return "Fichier";
      case 8: return "Upload";
      case 9: return "Titre";
      default: return "Autre";
    }
  };

  // Préparer les options pour React Select
  const typeOptions = formData.data.survey.JsonContent
    .filter((element, index, array) =>
      array.findIndex(item => item.type === element.type) === index
    )
    .map((element) => ({
      value: element.type,
      label: getQuestionType(element.type)
    }));

  // Ajouter une option "Tous" au début
  const allTypeOptions = [
    { value: '', label: 'Tous les types' },
    ...typeOptions
  ];

  // Fonction pour afficher les réponses d'une question
  const renderAnswers = (questionId, filteredFormData) => {
    if (!filteredFormData || !filteredFormData.data.answers) return null;

    const answersArray = [];

    filteredFormData.data.answers.forEach(answer => {
      if (answer.Answers[questionId]) {
        let promoterName;
        if (answer.PromoterName) {
          promoterName = answer.PromoterName;
        } else {
          promoterName = 'Promoteur inconnu';
        }

        // Gérer les différents types de valeurs
        let displayValue = answer.Answers[questionId];

        // Si c'est un objet (ex: pour les questions de distribution), le convertir en string lisible
        if (typeof displayValue === 'object' && displayValue !== null) {
          // Log pour debugger les objets dans renderAnswers
          console.warn('⚠️ OBJET DÉTECTÉ dans renderAnswers:', {
            questionId,
            questionType: 'Fallback rendering (type inconnu)',
            originalValue: displayValue,
            valueType: typeof displayValue,
            isArray: Array.isArray(displayValue),
            keys: Object.keys(displayValue),
            surveySentId: answer.SurveySentId,
            promoter: answer.PromoterName || 'Promoteur inconnu',
            answerDate: answer.AnswerDate
          });

          if (Array.isArray(displayValue)) {
            displayValue = displayValue.join(', ');
          } else {
            // Pour les objets de distribution, afficher chaque paire clé-valeur
            displayValue = Object.entries(displayValue)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
          }
        }

        answersArray.push({
          value: displayValue,
          promoter: promoterName,
          date: safeParseDate(answer.AnswerDate)?.toLocaleDateString('fr-FR') || 'Date invalide'
        });
      }
    });

    if (answersArray.length === 0) return <p className="text-gray-500 italic">Aucune réponse</p>;

    return (
      <div className="space-y-2 mt-2">
        {answersArray.map((answer, idx) => (
          <div key={idx} className="bg-gray-50 dark:bg-gray-800 p-2 rounded-md">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {answer.promoter} - {answer.date}
            </div>
            <div className="text-gray-800 dark:text-gray-200">
              {answer.value || <span className="italic text-gray-500">Réponse vide</span>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const visibleQuestions = React.useMemo(() => {
    const json = formData?.data?.survey?.JsonContent || [];
    return json
      .filter((q) => (filterByType ? q.type === parseInt(filterByType) : true))
      .filter((q) => q.type !== 7 && q.type !== 8); // ignore files/uploads
  }, [formData, filterByType]);

  const visibleCollapsibleQuestionIds = React.useMemo(() => {
    // We don't collapse titles (type 9), as they act as separators/headings.
    return visibleQuestions
      .filter((q) => q.type !== 9)
      .map((q) => q.Id?.toString())
      .filter(Boolean);
  }, [visibleQuestions]);

  const isCollapsed = React.useCallback((questionId) => {
    return !!collapsedMap[String(questionId)];
  }, [collapsedMap]);

  const toggleCollapsed = React.useCallback((questionId) => {
    const key = String(questionId);
    setCollapsedMap((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const collapseAll = React.useCallback(() => {
    setCollapsedMap((prev) => {
      const next = { ...prev };
      visibleCollapsibleQuestionIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
  }, [visibleCollapsibleQuestionIds]);

  const expandAll = React.useCallback(() => {
    setCollapsedMap((prev) => {
      const next = { ...prev };
      visibleCollapsibleQuestionIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  }, [visibleCollapsibleQuestionIds]);

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-gray-700 dark:text-gray-300">Filtrer par type de question :</span>
          <Select
            value={allTypeOptions.find(option => option.value === filterByType) || allTypeOptions[0]}
            onChange={(selectedOption) => setFilterByType(selectedOption?.value || '')}
            options={allTypeOptions}
            styles={selectStyles}
            placeholder="Filtrer par type..."
            isClearable={false}
            isSearchable={false}
            className="text-sm min-w-48"
          />
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={expandAll}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Tout déplier
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Tout replier
          </button>
        </div>
      </div>
      <div className="space-y-6">
        {visibleQuestions.map((question) => {
          // Ignorer les fichiers (type 7 et 8)
          if (question.type === 7 || question.type === 8)
            return null;

          // Ignorer les instructions/titres pour l'affichage détaillé
          if (question.type === 9) {
            // // do not display title if level is greater than 2 (useless descriptions)
            // if (question.level > 2)
            //   return null;

            return (
              <div
                key={question.Id}
                className={`p-3 ${question.bold ? 'font-bold' : ''} ${question.italic ? 'italic' : ''}
                ${question.underline ? 'underline' : ''} ${
                  question.level === 1 ? 'text-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' :
                  question.level === 2 ? 'text-base bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200' :
                  'text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300'
                } rounded-md`}
              >
                {question.question}
              </div>
            );
          }

          // Pour les questions numériques, utiliser le composant spécifique
          if (question.type === 2) {
            return (
              <GINumericQuestion
                key={question.Id}
                questionId={question.Id.toString()}
                questionText={question.question}
                answers={filteredFormData.data.answers}
                promoters={filteredFormData.data.promoters}
                isCollapsed={isCollapsed(question.Id)}
                onToggleCollapse={() => toggleCollapsed(question.Id)}
              />
            );
          }

          // Pour les questions de type choix, utiliser le composant spécifique
          if (question.type === 1) {
            return (
              <GIChoiceQuestion
                key={question.Id}
                questionId={question.Id.toString()}
                questionText={question.question}
                questionOptions={question.options}
                answers={filteredFormData.data.answers}
                isCollapsed={isCollapsed(question.Id)}
                onToggleCollapse={() => toggleCollapsed(question.Id)}
              />
            );
          }

          // Pour les questions de type texte long, utiliser le composant spécifique
          if (question.type === 6) {
            return (
              <GILongTextQuestion
                key={question.Id}
                questionId={question.Id.toString()}
                questionText={question.question}
                answers={filteredFormData.data.answers}
                fetchWithAuth={fetchWithAuth}
                campaignId={selectedCampaign?.value}
                currentUser={currentUser}
                isCollapsed={isCollapsed(question.Id)}
                onToggleCollapse={() => toggleCollapsed(question.Id)}
              />
            );
          }

          // Pour les questions de type Distribution (type 4), utiliser le composant spécifique
          if (question.type === 4) {
            return (
              <GIDistributionQuestion
                key={question.Id}
                questionId={question.Id.toString()}
                questionText={question.question}
                questionOptions={question.options}
                questionConstraints={{
                  min: question.min,
                  max: question.max,
                  sum: question.sum,
                  sumcalc: question.sumcalc
                }}
                answers={filteredFormData.data.answers}
                isCollapsed={isCollapsed(question.Id)}
                onToggleCollapse={() => toggleCollapsed(question.Id)}
              />
            );
          }

          // Pour les autres types de questions, afficher normalement
          const collapsed = isCollapsed(question.Id);
          return (
            <div key={question.Id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <GIQuestionHeader
                questionId={question.Id.toString()}
                questionText={question.question}
                questionType={getQuestionType(question.type)}
                badgeLabel={question.type === 3 ? 'Texte' : 'Autre'}
                isCollapsed={collapsed}
                onToggleCollapse={() => toggleCollapsed(question.Id)}
              />

              {!collapsed && (
                <>
                  {/* Affichage des options pour les questions à choix */}
                  {question.options && (
                    <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 p-4">
                      <div className="text-sm">
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Options:</p>
                        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400">
                          {question.options.map((option, idx) => (
                            <li key={idx}>{option.text}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    {renderAnswers(question.Id.toString(), filteredFormData)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GIFormResponses;